/**
 * TimeSavedChart — per-bucket "minutes saved" timeline (bug G5).
 *
 * Renders a BarChart of minutes-saved per bucket, converted to hours on the
 * Y axis (`minutes / 60`). Includes an info-icon hover that explains the
 * SSOT formula (mirrored from `src/services/analytics/queries.ts` ≈ lines
 * 68-140 — the `timeSavedMinutesExpr` doc block).
 *
 * State machine:
 *   - `loading`   → skeleton block, preserves layout height
 *   - `error`     → "awaiting backend" notice with the endpoint name so the
 *                   reviewer knows which API to wire next
 *   - `empty`     → "no data" hint (no bars)
 *   - happy path  → BarChart + summary total in the header
 *
 * Presentational: data fetching lives in `useTimeSavedSeries` (below) so the
 * chart can be unit-tested with canned fixtures the same way `TrendChart`
 * is.
 */

import * as React from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from 'recharts';

import type { TimeSavedSeries } from '@/services/api-types';
import {
  Tooltip as UiTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
// bug F1 — share visual contract with every other chart tooltip.
import {
  TOOLTIP_CLASSNAME,
  useChartTooltipTrigger,
  useIsTouchOnly,
} from '@/components/charts/ChartTooltip';
import { InteractiveChartHint } from '@/components/charts/InteractiveChartHint';
import { chartPalette } from '@/lib/chart-palette';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// P12 — delegate to the central formatter so hourly buckets include day+time
// ("Apr 14 15:00") instead of being ambiguous. Auto-detection on the ISO
// bucket handles the case where the caller doesn't know its granularity.
import { formatBucketLabel as formatBucketLabelShared } from '@/lib/format-bucket';
function formatBucketLabel(bucket: string): string {
  return formatBucketLabelShared(bucket, 'auto');
}

function minutesToHours(minutes: number): number {
  return minutes / 60;
}

// ---------------------------------------------------------------------------
// Tooltip
// ---------------------------------------------------------------------------

function ChartTooltip({
  active,
  payload,
  label,
}: TooltipProps<number, string>): React.ReactElement | null {
  if (!active || !payload || payload.length === 0) return null;
  // Stacked view — read both series from the first row's `payload`
  // (recharts exposes the full datum via `payload[i].payload`, identical
  // across series entries because they stack on the same bucket).
  const row = (payload[0]?.payload ?? {}) as {
    hours?: number;
    rmHours?: number;
    nonRmHours?: number;
    contactsHours?: number;
  };
  const totalHours = Number(row.hours ?? 0);
  const contactsHours = Number(row.contactsHours ?? 0);
  const rmHours = Number(row.rmHours ?? 0);
  const nonRmHours = Number(row.nonRmHours ?? 0);
  const totalMinutes = totalHours * 60;
  // Split rows drop out silently when the corresponding series is empty for
  // this bucket — a stacked bar with a 0-hour RM slice rendered "RM: 0.0h ·
  // Non-RM: 2.4h" with a visually orphaned zero; pruning is cleaner.
  const splitRows: Array<{
    key: string;
    label: string;
    hours: number;
    swatch: string;
  }> = [];
  if (rmHours > 0) {
    splitRows.push({
      key: 'rm',
      label: 'RM',
      hours: rmHours,
      swatch: chartPalette.rmTimeSaved,
    });
  }
  if (nonRmHours > 0) {
    splitRows.push({
      key: 'non-rm',
      label: 'Non-RM',
      hours: nonRmHours,
      swatch: chartPalette.nonRmTimeSaved,
    });
  }
  // Contacts are INSIDE the two rows above (a batch belongs to an RM or not), so this row reads
  // «of which», and the chart draws it as a line beside the bars, never stacked on them.
  if (contactsHours > 0) {
    splitRows.push({
      key: 'contacts',
      label: 'of which Contacts',
      hours: contactsHours,
      swatch: chartPalette.companion,
    });
  }

  return (
    // `min-w-[12rem]` anchors the split rows' right-column alignment so the
    // hours values stack in a tidy column instead of drifting with label
    // length. `text-left` defeats the centered-default on `TooltipContent` —
    // the default is fine for one-line chart tooltips, but this one carries
    // a label row + split rows and reads better left-aligned.
    <div
      data-testid="time-saved-tooltip"
      className={`${TOOLTIP_CLASSNAME} min-w-[12rem] text-left`}
    >
      {/* Bucket label — the "when" of this bar. Bold for hierarchy. */}
      <div className="mb-1.5 font-medium leading-tight">
        {typeof label === 'string' ? formatBucketLabel(label) : ''}
      </div>

      {/* Primary line — the headline number. Bold hours + muted minutes
          gloss so the eye lands on "2.8h" first and only drops to the
          minute-precision clarification after. */}
      <div className="tabular-nums leading-tight">
        <span className="text-muted-foreground">Total </span>
        <span className="font-semibold">{totalHours.toFixed(1)}h</span>
        <span className="text-muted-foreground">
          {' '}
          · {Math.round(totalMinutes)}m saved
        </span>
      </div>

      {/* Split rows — only when there's a real split. If a bucket is 100%
          RM the Non-RM row is omitted (and vice versa) so zero-rows never
          orphan. Grid layout keeps the swatch + label on the left and the
          hour value right-aligned on a shared axis. */}
      {splitRows.length > 0 && (
        <div
          className="mt-1.5 grid grid-cols-[auto_1fr_auto] items-center gap-x-2 gap-y-0.5 border-t pt-1 text-[11px]"
          data-testid="time-saved-tooltip-split"
        >
          {splitRows.map((r) => (
            <React.Fragment key={r.key}>
              <span
                aria-hidden
                className="inline-block h-2 w-2 rounded-sm"
                style={{ background: r.swatch }}
              />
              <span className="text-muted-foreground">{r.label}</span>
              <span className="tabular-nums font-medium">
                {r.hours.toFixed(1)}h
              </span>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface TimeSavedChartProps {
  /** Pre-fetched series; when present, hook wiring is skipped. */
  data?: TimeSavedSeries;
  loading?: boolean;
  /** Short message to surface when the fetch failed. */
  error?: string | null;
  /** Pixel height of the bar chart area. Default 220 per IA §2.2.2. */
  height?: number;
  /**
   * v1.4 dashboard-ux-fix (Task 3): click a bar → open a drill-down modal
   * on OverviewPage. The callback receives the exact ISO bucket string the
   * chart rendered (not a display label) so the backend can narrow its
   * time-window query without re-parsing the axis. `null` / undefined keeps
   * the chart non-interactive (classic layout).
   */
  onBucketClick?: (bucket: string) => void;
}

export function TimeSavedChart({
  data,
  loading = false,
  error = null,
  height = 220,
  onBucketClick,
}: TimeSavedChartProps): React.ReactElement {
  // Mobile tap persistence — see ChartTooltip.useChartTooltipTrigger.
  const tooltipTrigger = useChartTooltipTrigger();
  // On touch with a drill-down callback the Recharts Tooltip flashes
  // behind TimeSavedBreakdownModal's Radix backdrop — suppress it to
  // keep the UX readable. Same pattern as RatingDistribution /
  // LatencyDistribution. See commit note 2026-04-24.
  const isTouchOnly = useIsTouchOnly();
  const buckets = data?.buckets ?? [];
  const totalMinutes = buckets.reduce(
    (acc, b) => acc + (b.timeSavedMinutes ?? 0),
    0,
  );
  const totalRmMinutes = buckets.reduce(
    (acc, b) => acc + (b.rmMinutes ?? 0),
    0,
  );
  const totalNonRmMinutes = buckets.reduce(
    (acc, b) => acc + (b.nonRmMinutes ?? 0),
    0,
  );
  const totalContactsMinutes = buckets.reduce(
    (acc, b) => acc + (b.contactsMinutes ?? 0),
    0,
  );
  const totalHours = minutesToHours(totalMinutes);
  const totalRmHours = minutesToHours(totalRmMinutes);
  const totalNonRmHours = minutesToHours(totalNonRmMinutes);

  // Transform buckets into the recharts row shape (hours on the Y axis keeps
  // the number small; tooltip still shows raw minutes). `rmHours` /
  // `nonRmHours` are the stacked-series dataKeys; `hours` stays as the total
  // for the tooltip header and historical parity with downstream consumers.
  const rows = buckets.map((b) => ({
    bucket: b.t,
    minutes: b.timeSavedMinutes,
    hours: minutesToHours(b.timeSavedMinutes),
    rmHours: minutesToHours(b.rmMinutes ?? 0),
    nonRmHours: minutesToHours(b.nonRmMinutes ?? 0),
    contactsHours: minutesToHours(b.contactsMinutes ?? 0),
  }));

  return (
    <div data-testid="time-saved-chart" className="w-full space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium text-muted-foreground">
          <span>Time saved timeline</span>
          {onBucketClick && (
            <InteractiveChartHint data-testid="time-saved-hint" />
          )}
          <TooltipProvider delayDuration={200}>
            <UiTooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="About time saved"
                  data-testid="time-saved-info"
                  className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-muted-foreground/40 text-[10px] leading-none text-muted-foreground/70 hover:text-foreground"
                >
                  i
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="w-[50vw] max-w-[600px] text-left"
              >
                <div className="space-y-1.5 text-xs leading-snug">
                  <div>
                    Hours saved per bucket — matches the{' '}
                    <strong>/report</strong> Slack command formula.
                  </div>
                  <div>
                    Per-trace minutes (brief 10 / weekly 20; reactive
                    simple 1, medium 10, complex 20) × rating multiplier
                    (5→1.0, 4→0.7, 3→0.3, else 0), plus flat bonuses
                    for Jira-prefill opens (+5) and KB publishes (+15).
                  </div>
                  <div>
                    Contacts added to CRM: +2 per contact created, +1 per
                    contact CRM already had, no rating multiplier; a
                    spreadsheet batch at most 30 minutes. The Contacts line is
                    the part of each bar they earned.
                  </div>
                </div>
              </TooltipContent>
            </UiTooltip>
          </TooltipProvider>
        </div>
        {!loading && !error && buckets.length > 0 && (
          <div
            data-testid="time-saved-total"
            className="text-xs tabular-nums text-muted-foreground"
          >
            <span style={{ color: chartPalette.rmTimeSaved }}>RM</span>:{' '}
            <span className="font-semibold text-foreground">
              {totalRmHours.toFixed(1)}h
            </span>
            {' · '}
            <span style={{ color: chartPalette.nonRmTimeSaved }}>Non-RM</span>:{' '}
            <span className="font-semibold text-foreground">
              {totalNonRmHours.toFixed(1)}h
            </span>
            {totalContactsMinutes > 0 && (
              <>
                {' · of which '}
                <span style={{ color: chartPalette.companion }}>Contacts</span>:{' '}
                <span
                  data-testid="time-saved-total-contacts"
                  className="font-semibold text-foreground"
                >
                  {minutesToHours(totalContactsMinutes).toFixed(1)}h
                </span>
              </>
            )}
            {' · '}
            Total:{' '}
            <span className="font-semibold text-foreground">
              {totalHours.toFixed(1)}h
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <div
          data-testid="time-saved-skeleton"
          className="w-full rounded bg-muted animate-pulse"
          style={{ height }}
        />
      ) : error ? (
        <div
          data-testid="time-saved-error"
          className="flex items-center justify-center w-full rounded border border-dashed text-xs text-muted-foreground px-3"
          style={{ height }}
        >
          {error}
        </div>
      ) : buckets.length === 0 ? (
        <div
          data-testid="time-saved-empty"
          className="flex items-center justify-center w-full text-sm text-muted-foreground"
          style={{ height }}
        >
          No time-saved data in selected period
        </div>
      ) : (
        <div style={{ width: '100%', height }}>
          <ResponsiveContainer width="100%" height={height}>
            <ComposedChart
              data={rows}
              margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="bucket"
                tickFormatter={(v: string) => formatBucketLabel(v)}
              />
              <YAxis
                tickFormatter={(v: number) => `${v.toFixed(1)}h`}
                // 48 clipped the 3-digit top tick ("120.0h" ≈ 49px rendered at
                // x=-9, so it read as "20.0h"). 56 fits up to "999.9h". QA 2026-06-19.
                width={56}
              />
              {!(isTouchOnly && onBucketClick) && (
                <Tooltip
                  {...tooltipTrigger}
                  content={<ChartTooltip />}
                  cursor={false}
                />
              )}
              <Legend />
              {/* Stacked RM / Non-RM split. Palette shifts to GREEN here
                  (not the blue used on Requests chart) because time saved
                  is a positive-outcome metric and the whole chart should
                  read as "this is a win" — per the palette SSOT's
                  positive/neutral semantic split. RM = green-600 (hero),
                  Non-RM = green-300 (softer — still green so the chart
                  stays on-brand, lighter so the split reads). Both <Bar>
                  elements are direct children of BarChart (no Fragment) —
                  recharts identifies them by component type and loses them
                  silently if wrapped. */}
              <Bar
                dataKey="rmHours"
                stackId="time"
                fill={chartPalette.rmTimeSaved}
                name="RM"
                isAnimationActive={false}
                activeBar={
                  onBucketClick
                    ? { stroke: 'hsl(var(--foreground))', strokeWidth: 1.5 }
                    : false
                }
                cursor={onBucketClick ? 'pointer' : undefined}
                onClick={
                  onBucketClick
                    ? (payload: unknown) => {
                        const p = payload as { bucket?: unknown } | undefined;
                        if (p && typeof p.bucket === 'string') {
                          onBucketClick(p.bucket);
                        }
                      }
                    : undefined
                }
              />
              <Bar
                dataKey="nonRmHours"
                stackId="time"
                fill={chartPalette.nonRmTimeSaved}
                name="Non-RM"
                isAnimationActive={false}
                activeBar={
                  onBucketClick
                    ? { stroke: 'hsl(var(--foreground))', strokeWidth: 1.5 }
                    : false
                }
                cursor={onBucketClick ? 'pointer' : undefined}
                onClick={
                  onBucketClick
                    ? (payload: unknown) => {
                        const p = payload as { bucket?: unknown } | undefined;
                        if (p && typeof p.bucket === 'string') {
                          onBucketClick(p.bucket);
                        }
                      }
                    : undefined
                }
              />
              {/* The contacts share of each bar — a line, because it is already inside the
                  stacked bars; stacking it would count it twice. Drawn only when some bucket
                  has any, so a window before contacts existed keeps its old legend. */}
              {totalContactsMinutes > 0 && (
                <Line
                  dataKey="contactsHours"
                  name="of which Contacts"
                  type="monotone"
                  stroke={chartPalette.companion}
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  isAnimationActive={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
